import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminGetStats } from '../../api/admin';
import type { CompanyStat } from '../../api/admin';
import { useToastStore } from '../../store/useToastStore';
import { TH } from './shared';

export default function StatsTab() {
  const addToast = useToastStore((s) => s.addToast);
  const [stats, setStats] = useState<CompanyStat[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStats(await adminGetStats());
    } catch {
      addToast('Fehler beim Laden der Statistiken.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => ({
    totalTracked: stats.reduce((s, c) => s + c.totalTracked, 0),
    done: stats.reduce((s, c) => s + c.done, 0),
    pending: stats.reduce((s, c) => s + c.pending, 0),
    auditRecent: stats.reduce((s, c) => s + c.auditRecent, 0),
  }), [stats]);

  if (loading) return <div className="text-center py-5"><span className="spinner-border" /></div>;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <span className="fw-semibold">Alle Unternehmen</span>
        <button className="btn btn-outline-secondary btn-sm" onClick={load}>Aktualisieren</button>
      </div>

      <div className="row g-3 mb-4">
        {[
          { label: 'Erfasste Anrufe', value: totals.totalTracked, color: '' },
          { label: 'Erledigt', value: totals.done, color: 'text-success' },
          { label: 'Ausstehend', value: totals.pending, color: 'text-warning' },
          { label: 'Aktionen (7 Tage)', value: totals.auditRecent, color: '' },
        ].map(({ label, value, color }) => (
          <div key={label} className="col-6 col-md-3">
            <div className="border rounded-3 p-3 text-center" style={{ background: 'var(--bs-tertiary-bg)' }}>
              <div className={`fs-3 fw-bold ${color}`}>{value}</div>
              <div className="text-muted small">{label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.83rem' }}>
          <thead style={TH}>
            <tr>
              <th className="ps-3">Unternehmen</th>
              <th className="text-center">Erfasst</th>
              <th className="text-center">Erledigt</th>
              <th className="text-center">Ausstehend</th>
              <th>Fortschritt</th>
              <th className="text-center">Aktionen (7 Tage)</th>
              <th className="text-center pe-3">Gesamt</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((c) => {
              const pct = c.totalTracked ? Math.round((c.done / c.totalTracked) * 100) : 0;
              return (
                <tr key={c.companyId}>
                  <td className="ps-3 fw-semibold">{c.companyName}</td>
                  <td className="text-center">{c.totalTracked}</td>
                  <td className="text-center text-success">{c.done}</td>
                  <td className="text-center" style={{ color: c.pending > 0 ? 'var(--bs-warning)' : undefined }}>{c.pending}</td>
                  <td style={{ minWidth: 120 }}>
                    <div className="d-flex align-items-center gap-2">
                      <div className="progress flex-grow-1" style={{ height: 6 }}>
                        <div className="progress-bar bg-success" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-muted" style={{ fontSize: '0.72rem', minWidth: 28 }}>{pct}%</span>
                    </div>
                  </td>
                  <td className="text-center">{c.auditRecent}</td>
                  <td className="text-center pe-3 text-muted">{c.auditTotal}</td>
                </tr>
              );
            })}
            {stats.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-4">Keine Unternehmen</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
